// src/components/MembershipForm.js
import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { FaArrowLeft, FaArrowRight } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';

// Import all the form step components
import PersonalInfo from './PersonalInfo';
import ProfessionalDetails from './ProfessionalDetails';
import MembershipType from './MembershipType';
import EducationalBackground from './EducationalBackground';

// Define the initial state outside the component for reusability
const getInitialState = (user = {}) => ({
  profilePicture: null,
  title: '',
  firstName: user?.firstName || '',
  lastName: user?.lastName || '',
  email: user?.email || '',
  phone: '',
  dateOfBirth: '',
  gender: '',
  streetAddress: '',
  city: '',
  stateProvince: '',
  postalCode: '',
  country: '',
  license: '',
  governingBody: '',
  employmentStatus: '',
  jobRole: '',
  experience: '',
  specialization: [],
  otherSpecialization: '',
  highestEducation: '',
  institution: '',
  graduationYear: '',
  programAccreditation: '',
  certifications: '',
  expectedCompletionDate: '', // For student members
  membershipType: '', // This is key - starts empty until user selects
  membershipDuration: '1 Year',
  communication: ['ARCA Newsletter', 'Event & webinar updates']
});


const MembershipForm = ({ user, onSignOut }) => {
  const [formData, setFormData] = useState(getInitialState(user));
  const [currentStep, setCurrentStep] = useState(0);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [preview, setPreview] = useState(null); // State for image preview is now here

  // Dynamic steps based on membership type
  const getSteps = () => {
    return [
      'Membership Type Selection',
      'Personal Information',
      'Professional Details',
      'Educational Background'
    ];
  };

  const steps = getSteps();

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));

    // Clear the error for the field being changed
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const handleFileChange = (field, file) => {
    setFormData(prev => ({ ...prev, [field]: file }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const handleArrayChange = (field, value, checked) => {
    setFormData(prev => {
      const currentArray = prev[field] || [];
      const updatedArray = checked
        ? [...currentArray, value]
        : currentArray.filter(item => item !== value);
      return { ...prev, [field]: updatedArray };
    });
  };

  const validateStep = (stepIndex) => {
    const newErrors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const currentYear = new Date().getFullYear();

    console.log('🔍 Validating step:', stepIndex, 'membershipType:', formData.membershipType);
    console.log('📝 Current form data:', JSON.stringify(formData, null, 2));
    console.log('📋 Required fields for this step:');

    switch (stepIndex) {
      case 0: // Membership Type Selection (for all users)
        if (!formData.membershipType) newErrors.membershipType = 'Please select a membership type.';
        break;
      case 1: // Personal Information (for regular members)
        if (!formData.title) newErrors.title = 'Title is required.';
        if (!formData.firstName) newErrors.firstName = 'First name is required.';
        if (!formData.lastName) newErrors.lastName = 'Last name is required.';
        if (!formData.email) newErrors.email = 'Email is required.';
        else if (!emailRegex.test(formData.email)) newErrors.email = 'Please enter a valid email address.';
        if (!formData.phone) newErrors.phone = 'Phone number is required.';
        else if (formData.phone.replace(/[^0-9]/g, '').length < 7) newErrors.phone = 'Phone number must be at least 7 digits.';
        else if (formData.phone.replace(/[^0-9]/g, '').length > 15) newErrors.phone = 'Phone number must not exceed 15 digits.';
        if (!formData.dateOfBirth) newErrors.dateOfBirth = 'Date of birth is required.';
        if (!formData.gender) newErrors.gender = 'Gender is required.';
        if (!formData.streetAddress) newErrors.streetAddress = 'Street address is required.';
        if (!formData.city) newErrors.city = 'City is required.';
        if (!formData.stateProvince) newErrors.stateProvince = 'State / Province is required.';
        if (!formData.postalCode) newErrors.postalCode = 'Postal code is required.';
        if (!formData.country) newErrors.country = 'Country is required.';
        break;
      case 2: // Professional Details
        console.log('🔍 Validating Professional Details:');
        console.log('  membershipType:', formData.membershipType);
        console.log('  employmentStatus:', formData.employmentStatus);
        console.log('  jobRole:', formData.jobRole);
        console.log('  experience:', formData.experience);

        // Employment Status and Job Role are NOT required for student members
        const isStudent = formData.membershipType === 'student';

        if (!isStudent) {
          // Only validate employment status for non-student members
          if (!formData.employmentStatus) newErrors.employmentStatus = 'Employment status is required.';
          if (!['unemployed', 'student', 'retired'].includes(formData.employmentStatus) && (!formData.jobRole || formData.jobRole.trim() === '')) {
            console.log('❌ jobRole required but missing! employmentStatus:', formData.employmentStatus, 'jobRole:', JSON.stringify(formData.jobRole));
            newErrors.jobRole = 'Primary job role is required.';
          }
        }

        // Years of experience is still required for all members (including students)
        if (!formData.experience) newErrors.experience = 'Years of experience are required.';
        break;
      case 3: // Educational Background
        if (!formData.highestEducation) newErrors.highestEducation = 'Highest education level is required.';
        if (!formData.institution) newErrors.institution = 'Institution name is required.';

        // CRITICAL VALIDATION: Student membership eligibility based on education level
        // Students can pursue: Senior High, Diploma, or Bachelor's (BSC)
        // MSC, PhD and higher require Professional Membership
        if (formData.membershipType === 'student') {
          const studentEligibleLevels = ['senior_high', 'diploma', 'bachelor'];
          if (formData.highestEducation && !studentEligibleLevels.includes(formData.highestEducation)) {
            newErrors.highestEducation = 'Student membership is available for Senior High School, Diploma, or Bachelor\'s Degree (BSC). MSC, PhD and higher education levels require Professional Membership.';
          }
        }

        // Graduation year is only required for NON-STUDENTS
        if (formData.membershipType !== 'student') {
          if (!formData.graduationYear) newErrors.graduationYear = 'Graduation year is required.';
          else if (isNaN(formData.graduationYear) || formData.graduationYear < 1950 || formData.graduationYear > currentYear) {
            newErrors.graduationYear = `Please enter a valid year between 1950 and ${currentYear}.`;
          }
        }

        // Validate expected completion date for students
        if (formData.membershipType === 'student') {
          if (!formData.expectedCompletionDate) {
            newErrors.expectedCompletionDate = 'Expected completion date is required for student members.';
          }
        }
        break;
      default:
        break;
    }

    console.log('❌ Validation errors found:', JSON.stringify(newErrors, null, 2));
    console.log('📊 Error count:', Object.keys(newErrors).length);
    return newErrors;
  };


  const nextStep = () => {
    console.log('🚀 Attempting to go to next step from:', currentStep);
    console.log('📋 Current formData:', JSON.stringify(formData, null, 2));

    const stepErrors = validateStep(currentStep);
    console.log('🔍 Step validation errors:', JSON.stringify(stepErrors, null, 2));

    if (Object.keys(stepErrors).length === 0) {
      console.log('✅ No errors, proceeding to next step');
      setErrors({}); // Clear old errors
      if (currentStep < steps.length - 1) {
        setCurrentStep(prev => prev + 1);
        console.log('🎯 Moved to step:', currentStep + 1);
      }
    } else {
      console.log('❌ Validation failed, setting errors:', JSON.stringify(stepErrors, null, 2));
      setErrors(stepErrors);
      toast.error('Please correct the errors before proceeding.');
    }
  };

  const prevStep = () => {
    if (currentStep > 0) setCurrentStep(prev => prev - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const finalErrors = validateStep(currentStep);
    if (Object.keys(finalErrors).length > 0) {
      setErrors(finalErrors);
      toast.error('Please complete all required fields to submit.');
      return;
    }

    setIsSubmitting(true);
    toast.info('Submitting your application...');

    try {
      // Create FormData for file upload
      const dataToSubmit = new FormData();

      // Add all form fields to FormData with proper field name mapping
      for (const key in formData) {
        const value = formData[key];
        if (value === null || value === undefined) continue;

        if (Array.isArray(value)) {
          // Handle arrays (like specialization, communication, areasOfConcentration)
          value.forEach(item => dataToSubmit.append(key, item));
        } else if (key === 'profilePicture' && value instanceof File) {
          // Handle file upload
          dataToSubmit.append('profilePicture', value);
        } else {
          // Handle regular fields - convert camelCase to expected backend format if needed
          let fieldName = key;
          let fieldValue = value.toString();

          // Normalize email before submission
          if (key === 'email') {
            fieldValue = fieldValue.trim().toLowerCase();
          }

          // Map frontend field names to backend expected names
          switch (key) {
            case 'membershipType':
              fieldName = 'membershipType';
              break;
            case 'membershipDuration':
              fieldName = 'membershipDuration';
              break;
            // All other fields keep their original names
            default:
              fieldName = key;
              break;
          }

          dataToSubmit.append(fieldName, fieldValue);
        }
      }

      // Debug: Log what we're sending
      console.log('🚀 Form data being submitted:');
      console.log('📋 membershipType:', formData.membershipType);
      console.log('📋 employmentStatus:', formData.employmentStatus);
      console.log('📋 jobRole:', formData.jobRole);
      console.log('👨‍🎓 expectedCompletionDate:', formData.expectedCompletionDate);
      for (let [key, value] of dataToSubmit.entries()) {
        console.log(`  ${key}: ${typeof value === 'object' ? '[File]' : value}`);
      }

      // Submit to backend API with better error handling
      const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      // Ensure API_BASE doesn't end with /api to avoid duplication
      const baseUrl = API_BASE.replace(/\/api$/, '');
      const endpoint = `${baseUrl}/api/members`;

      console.log('🌐 API Endpoint:', endpoint);

      const response = await fetch(endpoint, {
        method: 'POST',
        body: dataToSubmit,
        // Don't set Content-Type header - let browser set it for FormData
      });

      console.log('✅ Response status:', response.status);
      const result = await response.json();
      console.log('✅ Response data:', JSON.stringify(result, null, 2));

      if (!response.ok) {
        console.log('❌ Server validation errors:', result.errors || result.message);
        throw new Error(result.message || `Server error: ${response.status}`);
      }

      console.log('Form Submitted Successfully', result);
      toast.success(result.message || 'Registration submitted successfully! Please check your email to set your password.');

      // --- FORM RESET LOGIC ---
      setFormData(getInitialState(user)); // Reset form fields to initial state
      setCurrentStep(0);                   // Go back to the first step
      setPreview(null);                    // Clear the image preview
      // ------------------------

    } catch (error) {
      console.error('Submission Error:', error);

      // Handle different types of errors
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        toast.error('Unable to connect to server. Please ensure the backend is running.');
      } else {
        toast.error(`Submission failed: ${error.message}`);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const getStepComponents = () => {
    return [
      <MembershipType
        formData={formData}
        handleChange={handleChange}
        handleArrayChange={handleArrayChange}
        errors={errors}
      />,
      <PersonalInfo
        formData={formData}
        handleChange={handleChange}
        handleFileChange={handleFileChange}
        errors={errors}
        preview={preview}
        setPreview={setPreview}
      />,
      <ProfessionalDetails
        formData={formData}
        handleChange={handleChange}
        handleArrayChange={handleArrayChange}
        errors={errors}
      />,
      <EducationalBackground
        formData={formData}
        handleChange={handleChange}
        errors={errors}
      />
    ];
  };

  const stepComponents = getStepComponents();

  const slideAnimation = {
    initial: { opacity: 0, x: 50 },
    animate: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: -50 },
    transition: { duration: 0.3 }
  };

  return (
    <div className="p-4 p-md-5">
      <div className="container" style={{ maxWidth: '900px' }}>
        {/* Removed "Back to Sign-In" button */}

        <form onSubmit={handleSubmit} className="card shadow-lg border-0">
          <div className="card-body p-4 p-md-5">
            {/* Header */}
            <div className="text-center mb-4">
              <h1 className="display-5 fw-bold mb-2">
                <span className="text-success">A</span>R<span className="text-success">C</span>A{' '}
                <span className="text-danger">Membership Registration</span>
              </h1>
              <p className="text-muted small">Please complete all steps to join our community</p>
            </div>

            {/* Progress Bar */}
            <div className="mb-4">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <span className="small fw-semibold" style={{ color: '#6B8E23' }}>{steps[currentStep]}</span>
                <span className="small text-muted">Step {currentStep + 1} of {steps.length}</span>
              </div>
              <div className="progress" style={{ height: '8px' }}>
                <motion.div
                  className="progress-bar"
                  style={{ backgroundColor: '#6B8E23' }}
                  initial={{ width: 0 }}
                  animate={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
                  transition={{ duration: 0.5 }}
                  role="progressbar"
                  aria-valuenow={((currentStep + 1) / steps.length) * 100}
                  aria-valuemin="0"
                  aria-valuemax="100"
                />
              </div>
            </div>

            {/* Form Steps */}
            <AnimatePresence mode="wait">
              <motion.div key={currentStep} {...slideAnimation}>
                {stepComponents[currentStep]}
              </motion.div>
            </AnimatePresence>

            {/* Navigation Buttons */}
            <div className="d-flex justify-content-between mt-4 pt-4 border-top">
              {currentStep > 0 ? (
                <motion.button
                  type="button"
                  onClick={prevStep}
                  className="btn btn-outline-secondary d-flex align-items-center"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <FaArrowLeft className="me-2" /> Previous
                </motion.button>
              ) : <div />}

              {currentStep < steps.length - 1 ? (
                <motion.button
                  type="button"
                  onClick={nextStep}
                  className="btn d-flex align-items-center text-white"
                  style={{ backgroundColor: '#6B8E23', borderColor: '#6B8E23' }}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Next <FaArrowRight className="ms-2" />
                </motion.button>
              ) : (
                <motion.button
                  type="submit"
                  className="btn btn-success d-flex align-items-center"
                  style={{ backgroundColor: '#28a745' }}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Submitting...
                    </>
                  ) : (
                    'Submit Application'
                  )}
                </motion.button>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MembershipForm;